import { useId } from "react";
import { PhoneField } from "@/components/PhoneField";
import {
  EMPTY_PHONE,
  withOnePrimary,
  type PhoneLineType,
  type PhoneValue,
} from "@/lib/phone";

// BRK-S073 — the repeatable phone editor. PMS-S173 turned a single
// `primaryPhone` string into an array of numbers with one marked primary, on the
// account AND on every person, so this is the one place that UI lives (DRY: the
// account block and each personnel row render the same component).
//
// Two invariants it owns, because PMS rejects a body that breaks either:
//   • exactly one primary — enforced through withOnePrimary on every mutation,
//     so removing the primary row promotes a survivor rather than sending none;
//   • no phantom rows — a row left blank is dropped at submit (toPhoneInputs),
//     not validated, so "+ Add phone" is never a trap.
//
// A lone number is implicitly primary, so the radio + remove affordances only
// appear from the second row on — the common single-phone case stays exactly as
// simple as the old single field. Labels are indexed only when there is more than
// one row, keeping that case reading as plain "Phone".
export function PhoneListField({
  label,
  value,
  onChange,
  errors,
  required,
  addLabel = "+ Add phone",
}: {
  /** Base label for a row's number field ("Phone"), indexed past the first row. */
  label: string;
  value: PhoneValue[];
  onChange: (rows: PhoneValue[]) => void;
  /** Per-row rejection reasons, aligned with `value` (from phoneRowErrors). */
  errors?: (string | null)[];
  /** Marks the FIRST row required — the floor is "at least one number". Every
   *  row past the first is marked optional either way. */
  required?: boolean;
  addLabel?: string;
}) {
  // One radio group per instance, so two lists on the same page (the account's
  // and a person's) don't fight over which number is primary.
  const groupName = `${useId()}-primary`;
  const multiple = value.length > 1;

  // Choosing a primary DEMOTES its siblings here rather than leaning on
  // withOnePrimary, whose tie-break keeps the first already-marked row — which
  // would silently ignore the row the broker just picked.
  const update = (i: number, patch: Partial<PhoneValue>) =>
    onChange(
      withOnePrimary(
        value.map((r, idx) =>
          idx === i
            ? { ...r, ...patch }
            : patch.isPrimary
              ? { ...r, isPrimary: false }
              : r,
        ),
      ),
    );

  return (
    <div className="flex w-full flex-col gap-3">
      {value.map((rowValue, i) => (
        <div key={i} className="flex flex-col gap-2">
          <PhoneField
            label={multiple ? `${label} ${i + 1}` : label}
            value={rowValue.number}
            onChange={(number) => update(i, { number })}
            error={errors?.[i] ?? undefined}
            required={required && i === 0}
            optional={!required || i > 0}
            lineType={rowValue.lineType}
            onLineTypeChange={(lineType: PhoneLineType | "") =>
              update(i, { lineType })
            }
            lineTypeLabel={multiple ? `Line type ${i + 1}` : "Line type"}
          />
          {multiple && (
            <div className="flex flex-wrap items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-brk-text">
                <input
                  type="radio"
                  name={groupName}
                  checked={rowValue.isPrimary}
                  onChange={() => update(i, { isPrimary: true })}
                />
                Primary
              </label>
              <button
                type="button"
                onClick={() =>
                  onChange(withOnePrimary(value.filter((_, idx) => idx !== i)))
                }
                className="text-sm text-brk-danger hover:underline"
              >
                Remove
              </button>
            </div>
          )}
        </div>
      ))}
      <button
        type="button"
        onClick={() =>
          onChange([...value, { ...EMPTY_PHONE, isPrimary: false }])
        }
        className="self-start text-sm text-brk-accent hover:underline"
      >
        {addLabel}
      </button>
    </div>
  );
}
